<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue';
  import { ColoredButton, IconImage, TableTitle } from '@/shared/ui';
  import OrderIcon from '@/shared/assets/icons/Order.svg';
  import OrderWhite from '@/shared/assets/icons/OrderWhite.svg';
  import FilterIcon from '@/shared/assets/icons/Filter.svg';
  import FilterWhite from '@/shared/assets/icons/FilterWhite.svg';
  import GrayTrashIcon from '@/shared/assets/icons/GrayTrashIcon.svg';
  import type { OrderOptions, FilterAndSortEmit } from '@/shared/lib';
  import { useFilterAndSort } from '@/shared/lib';

  interface Props {
    showGeneralColumns: boolean;
    orderOptions: OrderOptions[];
    currentSort?: string;
    currentOrder?: string;
    isFilterActive?: boolean;
    hasSelection?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    currentSort: undefined,
    currentOrder: undefined,
    isFilterActive: false,
    hasSelection: false,
  });

  const emit = defineEmits<{
    (e: 'update:sort', sort: string, order: string): void;
    (e: 'filter-clicked'): void;
    (e: 'delete-selected'): void;
  }>();

  const {
    isOrderSelected,
    isOrderDropdownOpen,
    selectedOption,
    dropdownPosition,
    orderButtonRef,
    handleOrder,
    sortOptions: applySortOption,
    handleClickOutside,
    hideModalOverlay,
  } = useFilterAndSort(emit as FilterAndSortEmit, {
    sort: () => props.currentSort,
    order: () => props.currentOrder,
    isActive: () => props.currentSort !== undefined && props.currentOrder !== undefined,
  });

  onMounted(() => {
    document.addEventListener('click', handleClickOutside);
  });

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
    hideModalOverlay();
  });
</script>

<template>
  <div class="flex items-center justify-between gap-3 px-6 py-4 border-b border-gray-02">
    <TableTitle text="Lista de Avisos" />
    <div class="flex items-center gap-3">
      <div class="relative">
        <div ref="orderButtonRef">
          <ColoredButton
            v-if="isOrderSelected"
            text="Ordenar por"
            :icon="OrderWhite"
            icon-alt="Ícone de ordenação"
            :outlined="false"
            @click="handleOrder"
          />
          <ColoredButton
            v-else
            text="Ordenar por"
            :icon="OrderIcon"
            icon-alt="Ícone de ordenação"
            :outlined="true"
            @click="handleOrder"
          />
        </div>

        <transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="transform opacity-0"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="transform opacity-0"
        >
          <div
            v-if="isOrderDropdownOpen"
            id="order-dropdown"
            class="fixed max-w-80 p-4 bg-white-01 rounded-2xl shadow-2xl z-50"
            :style="{ top: dropdownPosition.top + 'px', right: dropdownPosition.right + 'px' }"
          >
            <div class="py-1">
              <button
                v-for="option in props.orderOptions"
                :key="`${option.sort}:${option.order}`"
                type="button"
                class="block w-full text-left px-4 py-2 text-md text-gray-07 font-medium"
                :class="{
                  'bg-gray-02 rounded-lg':
                    selectedOption?.sort === option.sort && selectedOption?.order === option.order,
                }"
                @click="applySortOption(option.sort, option.order)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </transition>
      </div>
      <ColoredButton
        v-if="props.isFilterActive"
        text="Filtrar"
        :icon="FilterWhite"
        icon-alt="Filtrar"
        :outlined="false"
        @click="emit('filter-clicked')"
      />
      <ColoredButton
        v-else
        text="Filtrar"
        :icon="FilterIcon"
        icon-alt="Filtrar"
        :outlined="true"
        @click="emit('filter-clicked')"
      />
      <template v-if="props.showGeneralColumns">
        <button
          type="button"
          data-testid="general-delete-button"
          :disabled="!props.hasSelection"
          class="h-[42px] px-4 rounded-lg border border-gray-02 bg-white-01 text-gray-07 text-sm font-medium flex items-center gap-2 whitespace-nowrap shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] disabled:opacity-50 disabled:cursor-not-allowed"
          @click="emit('delete-selected')"
        >
          <IconImage
            :src="GrayTrashIcon"
            alt="Deletar"
            class="w-5 h-5"
          />
          <span>Deletar</span>
        </button>
      </template>
    </div>
  </div>
</template>
